/**
 * CEVRIMDISI SENKRON KUYRUGU
 *
 * Sisteme verilen soz (tanitim §18): "Girdigi veri telefonunda bekler ve
 * baglanti geldiginde otomatik gonderilir. Ogrenci bunun icin ekstra bir
 * sey yapmaz."
 *
 * TASARIM KARARLARI
 *
 * 1. Depolama SOYUTLANDI. Kuyruk mantigi IndexedDB bilmez; `KuyrukDeposu`
 *    arayuzunu bilir. Boylece butun kurallar tarayicisiz test edilir —
 *    veri kaybina yol acabilecek tek yer burasi oldugu icin bu onemli.
 *
 * 2. Her kayit `istemciKayitId` tasir ve bu kimlik ISTEMCIDE uretilir.
 *    Zayif baglantida istek gider ama yanit donmez; istemci tekrar dener.
 *    Sunucu ayni kimligi ikinci kez gordugunde yeni kayit acmaz.
 *
 * 3. `yapildiAt` islemin TELEFONDA yapildigi andir, sunucuya ulastigi an
 *    degil. Ogrenci aksam 21:00'de calisip veriyi ertesi sabah gonderse
 *    bile analiz dogru gune yazar.
 *
 * 4. Kalici red (`kalici: true`) ile gecici hata AYRILIR. Kalici redde
 *    kayit bir daha denenmez; aksi halde bozuk bir kayit kuyrugu sonsuza
 *    kadar tikar ve arkasindaki gecerli kayitlar da gitmez.
 */

import {
  senkronKaynakAnahtari,
  type KuyrukDurumu,
  type KuyrukIslemi,
  type KuyrukKaydi,
} from "@koclukpanel/contracts";

/** Kayda ozgu en fazla kac red sayilir — sonra kullaniciya gosterilir */
export const MAKS_DENEME = 5;

const ALTYAPI_BEKLEMELERI = [30_000, 120_000, 600_000] as const;

/**
 * Kuyrugun ihtiyac duydugu depolama sozlesmesi.
 * Uretimde IndexedDB, testte bellek.
 */
export interface KuyrukDeposu {
  hepsi(): Promise<KuyrukKaydi[]>;
  /** Sayaç artışı ve yeni kayıt aynı atomik işlemde kalıcılaşır. */
  ekle(kayit: Omit<KuyrukKaydi, "siraNo">): Promise<KuyrukKaydi>;
  yaz(kayit: KuyrukKaydi): Promise<void>;
  sil(istemciKayitId: string): Promise<void>;
}

export interface SenkronSonucGirdisi {
  kabul: readonly string[];
  reddedilen: readonly { istemciKayitId: string; mesaj: string; kalici: boolean; kod?: string }[];
}

export interface KimlikUretici {
  (): string;
}

export interface SaatKaynagi {
  (): Date;
}

export interface GonderimPartisi {
  sahipKullaniciId: string;
  partiId: string;
  kayitKimlikleri: readonly string[];
}

function eklenmeSirasi(a: KuyrukKaydi, b: KuyrukKaydi): number {
  // Eski kayıtlar yeni sayaçlı kayıtlardan önce gelir. Karma listede de
  // geçişli bir sıra gerekir; ikili tarih yedeği saat geri alınca bunu bozar.
  if (a.siraNo === undefined && b.siraNo === undefined)
    return a.yapildiAt.localeCompare(b.yapildiAt);
  if (a.siraNo === undefined) return -1;
  if (b.siraNo === undefined) return 1;
  return a.siraNo - b.siraNo;
}

export class SenkronKuyrugu {
  private readonly altyapiBeklemeleri = new Map<string, { adim: number; tekrarAt: number }>();

  constructor(
    private readonly depo: KuyrukDeposu,
    private readonly kimlikUret: KimlikUretici,
    private readonly aktifKullanici: () => string | null,
    private readonly simdi: SaatKaynagi = () => new Date(),
  ) {}

  private sahipZorunlu(): string {
    const sahip = this.aktifKullanici();
    if (!sahip) throw new Error("Kayıt için önce hesabınızın doğrulanması gerekiyor.");
    return sahip;
  }

  private async kayitlarim(): Promise<KuyrukKaydi[]> {
    const sahip = this.aktifKullanici();
    if (!sahip) return [];
    const kayitlar = await this.depo.hepsi();
    if (this.aktifKullanici() !== sahip) return [];
    return kayitlar.filter((kayit) => kayit.sahipKullaniciId === sahip);
  }

  /** Yeni bir islem kuyruga alinir ve iyimser olarak uygulanmis sayilir. */
  async ekle(islem: KuyrukIslemi, govde: Record<string, unknown>): Promise<KuyrukKaydi> {
    const kayit: KuyrukKaydi = {
      sahipKullaniciId: this.sahipZorunlu(),
      istemciKayitId: this.kimlikUret(),
      islem,
      govde,
      yapildiAt: this.simdi().toISOString(),
      durum: "bekliyor",
      denemeSayisi: 0,
      sonHata: null,
    };
    return this.depo.ekle(kayit);
  }

  /** Eklenme sırası korunur; bekleyen kaynak bağımsız kaynakları durdurmaz. */
  async gonderilecekler(): Promise<KuyrukKaydi[]> {
    const kayitlar = (await this.kayitlarim()).sort(eklenmeSirasi);
    const engelliKaynaklar = new Set<string>();
    const secilen: KuyrukKaydi[] = [];
    const simdi = this.simdi().getTime();
    for (const kayit of kayitlar) {
      if (kayit.durum === "hatali" || kayit.durum === "gonderildi") continue;
      const kaynak = senkronKaynakAnahtari(kayit.islem, kayit.govde);
      if (engelliKaynaklar.has(kaynak)) continue;
      if (
        kayit.durum !== "bekliyor" ||
        kayit.denemeSayisi >= MAKS_DENEME ||
        (this.altyapiBeklemeleri.get(kayit.istemciKayitId)?.tekrarAt ?? 0) > simdi
      ) {
        engelliKaynaklar.add(kaynak);
        continue;
      }
      secilen.push(kayit);
    }
    return secilen;
  }

  /** Kullaniciya gosterilecek kayitlar: bekleyen + hatali. */
  async ozet(): Promise<Record<KuyrukDurumu, number>> {
    const ozet = { bekliyor: 0, gonderiliyor: 0, gonderildi: 0, hatali: 0 };
    for (const kayit of await this.kayitlarim()) ozet[kayit.durum] += 1;
    return ozet;
  }

  // F-STV-11 — yalnız gönderim kilidini alan sekme yarım kalmış kayıtları kurtarır.
  async yarimKalanlariKurtar(): Promise<void> {
    const sahip = this.sahipZorunlu();
    for (const kayit of await this.kayitlarim()) {
      if (this.aktifKullanici() !== sahip) return;
      if (kayit.durum === "gonderiliyor")
        await this.depo.yaz({ ...kayit, durum: "bekliyor", gonderimPartisiId: undefined });
    }
  }

  /**
   * Gonderim baslarken isaretlenir — ayni kayit iki kez gonderilmesin.
   * 14.09.2026: parti kimligi de saklanir; eski yanit yeni gonderimi
   * etkileyemesin. Sekmeler arasi kilit bu isaretlemenin on kosuludur.
   */
  async gonderimeBasla(kayitlar: readonly KuyrukKaydi[]): Promise<GonderimPartisi> {
    const sahip = this.sahipZorunlu();
    const sirali = kayitlar.filter((kayit) => kayit.sahipKullaniciId === sahip).sort(eklenmeSirasi);
    const parti: GonderimPartisi = {
      sahipKullaniciId: sahip,
      partiId: this.kimlikUret(),
      kayitKimlikleri: sirali.map((kayit) => kayit.istemciKayitId),
    };
    for (const kayit of sirali) {
      if (this.aktifKullanici() !== sahip) break;
      if (kayit.sahipKullaniciId === sahip)
        await this.depo.yaz({ ...kayit, durum: "gonderiliyor", gonderimPartisiId: parti.partiId });
    }
    return parti;
  }

  /**
   * Sunucu yanitini uygular.
   *
   * - Kabul edilen kayit SILINIR (telefonda yer tutmasin).
   * - Kalici red: bir daha denenmez, kullaniciya gosterilir.
   * - Kayda ozgu gecici hata: deneme sayaci artar, tekrar "bekliyor"a doner.
   *   Sayac sinira ulasirsa "hatali" olur ve otomatik denenmez.
   *
   * 14.09.2026: yanit yalniz ayni sahip ve partiye uygulanir; aksi
   * halde geciken yanit yeni kaydi silebilirdi. Yanitsiz kayitlar ile
   * gecici OTURUM_DEGISTI/ISLENIYOR/SUNUCU_HATASI bekler ve sayac artmaz: bunlar
   * ogrencinin verisinin hatali oldugunu gostermiyor.
   */
  async sonucuIsle(parti: GonderimPartisi, sonuc: SenkronSonucGirdisi): Promise<void> {
    const kimlikler = new Set(parti.kayitKimlikleri);
    const kabul = new Set(sonuc.kabul);
    const redler = new Map(sonuc.reddedilen.map((red) => [red.istemciKayitId, red]));
    for (const kayit of await this.kayitlarim()) {
      if (this.aktifKullanici() !== parti.sahipKullaniciId) return;
      // F-STV-11 — eski veya başka partinin yanıtı yeni kaydı silemez ve kurtaramaz.
      if (
        !kimlikler.has(kayit.istemciKayitId) ||
        kayit.gonderimPartisiId !== parti.partiId ||
        kayit.durum !== "gonderiliyor"
      )
        continue;
      if (kabul.has(kayit.istemciKayitId)) {
        await this.depo.sil(kayit.istemciKayitId);
        this.altyapiBeklemeleri.delete(kayit.istemciKayitId);
        continue;
      }
      const red = redler.get(kayit.istemciKayitId);
      // D9 — geçici sunucu/oturum durumu kaydın deneme bütçesini tüketmez.
      // F-STV-04a — GELECEK_GUN da: telefon saati sunucudan ileriyse gece
      // yarısı civarında "bugün" girilen kayıt sunucuda henüz gelecek gündür;
      // birkaç dakika sonra AYNI kayıt kabul edilir. Bütçeden sayılsaydı hızlı
      // tekrarlar beş denemeyi bitirip geçerli veriyi "hatalı"ya düşürürdü
      // (ölçüldü 05.10.2026); geri çekilmeyle (30 sn → 2 dk → 10 dk) bekler.
      const sayilmayanRed =
        red?.kalici === false &&
        (red.kod === "OTURUM_DEGISTI" ||
          red.kod === "ISLENIYOR" ||
          red.kod === "SUNUCU_HATASI" ||
          red.kod === "GELECEK_GUN");
      const denemeSayisi = kayit.denemeSayisi + (red && !sayilmayanRed ? 1 : 0);
      await this.depo.yaz({
        ...kayit,
        gonderimPartisiId: undefined,
        denemeSayisi,
        sonHata: red?.mesaj ?? kayit.sonHata,
        durum:
          !sayilmayanRed && (red?.kalici || denemeSayisi >= MAKS_DENEME) ? "hatali" : "bekliyor",
      });
      if (sayilmayanRed) {
        // Yeni kayıt/bağlantı tetikleri de bu kaydı hemen tekrar gönderemesin.
        const adim = Math.min(
          (this.altyapiBeklemeleri.get(kayit.istemciKayitId)?.adim ?? -1) + 1,
          ALTYAPI_BEKLEMELERI.length - 1,
        );
        this.altyapiBeklemeleri.set(kayit.istemciKayitId, {
          adim,
          tekrarAt: this.simdi().getTime() + ALTYAPI_BEKLEMELERI[adim]!,
        });
      }
    }
  }

  /** Kullanici hatali bir kaydi elle tekrar denemek isterse. */
  async tekrarDene(istemciKayitId: string): Promise<void> {
    const kayit = (await this.kayitlarim()).find(
      (kayit) => kayit.istemciKayitId === istemciKayitId,
    );
    if (!kayit || kayit.durum !== "hatali" || this.aktifKullanici() !== kayit.sahipKullaniciId)
      return;
    await this.depo.yaz({ ...kayit, durum: "bekliyor", denemeSayisi: 0, sonHata: null });
  }

  /** Kullanici hatali kaydi tamamen vazgecerse. */
  async vazgec(istemciKayitId: string): Promise<void> {
    const kayit = (await this.kayitlarim()).find(
      (kayit) => kayit.istemciKayitId === istemciKayitId,
    );
    if (
      !kayit ||
      kayit.durum === "gonderiliyor" ||
      this.aktifKullanici() !== kayit.sahipKullaniciId
    )
      return;
    await this.depo.sil(istemciKayitId);
    this.altyapiBeklemeleri.delete(istemciKayitId);
  }
}
